/** Distribute a PATCH 400 body onto the notification editor's surfaces.
 *
 * Backend shape (apiv2/serializers/console/notifications.py):
 *   { notification?: {field: msg | {sub: msg}},
 *     delivery_methods?: { "<dm_id>": { "<field>": [{message, line}] } },
 *     block_refs?: { "<idx>": msg } | msg,
 *     rules?: { "<idx>": {field: [msg]} } | msg,
 *     delivery_method_advanced_rules?: { "<idx>": {field: msg} } | msg,
 *     auto_replies?: { options?: { "<idx>": {field: msg} }, <field>: msg } | msg,
 *     detail?: msg }
 */
import type { TemplateDiagnostic } from '@/components/TemplateCodeEditor';

export interface MappedErrors {
  /** Meta field errors keyed by NotificationCore field name. */
  meta: Record<string, string>;
  /** Per-DM, per-field template diagnostics for TemplateCodeEditor. */
  dmDiagnostics: Record<number, Record<string, TemplateDiagnostic[]>>;
  /** Block-ref errors keyed by row index ('*' = general). */
  blockRefs: Record<string, string>;
  /** Rule errors keyed by row index ('*' = general). */
  rules: Record<string, string>;
  /** Advanced-rule errors keyed by row index ('*' = general). */
  advancedRules: Record<string, string>;
  /** Auto-reply errors keyed by option index ('*' = general/object-level). */
  autoReplies: Record<string, string>;
  /** Anything unmapped — shown in a snackbar/alert. */
  general: string[];
}

function asMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(asMessage).join(' ');
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${asMessage(v)}`)
      .join('; ');
  }
  return String(value);
}

export function mapNotificationErrors(body: unknown): MappedErrors {
  const out: MappedErrors = { meta: {}, dmDiagnostics: {}, blockRefs: {}, rules: {}, advancedRules: {}, autoReplies: {}, general: [] };
  if (!body || typeof body !== 'object') {
    out.general.push('The save was rejected.');
    return out;
  }
  const b = body as Record<string, unknown>;

  if (b.notification && typeof b.notification === 'object') {
    for (const [field, msg] of Object.entries(b.notification as Record<string, unknown>)) {
      out.meta[field] = asMessage(msg);
    }
  }

  if (b.delivery_methods && typeof b.delivery_methods === 'object') {
    for (const [dmKey, fields] of Object.entries(b.delivery_methods as Record<string, unknown>)) {
      const dmId = Number(dmKey);
      if (!Number.isFinite(dmId) || !fields || typeof fields !== 'object') {
        out.general.push(`Delivery methods: ${asMessage(fields)}`);
        continue;
      }
      const perField: Record<string, TemplateDiagnostic[]> = {};
      for (const [field, errors] of Object.entries(fields as Record<string, unknown>)) {
        const list = Array.isArray(errors) ? errors : [errors];
        perField[field] = list.map((e) => {
          const rec = (e ?? {}) as { message?: unknown; line?: unknown };
          return {
            line: typeof rec.line === 'number' && rec.line > 0 ? rec.line : 1,
            severity: 'error' as const,
            message: typeof rec.message === 'string' ? rec.message : asMessage(e),
          };
        });
      }
      out.dmDiagnostics[dmId] = perField;
    }
  }

  if (b.block_refs !== undefined) {
    if (typeof b.block_refs === 'string') out.blockRefs['*'] = b.block_refs;
    else if (b.block_refs && typeof b.block_refs === 'object') {
      for (const [idx, msg] of Object.entries(b.block_refs as Record<string, unknown>)) {
        out.blockRefs[idx] = asMessage(msg);
      }
    }
  }

  if (b.rules !== undefined) {
    if (typeof b.rules === 'string') out.rules['*'] = b.rules;
    else if (Array.isArray(b.rules)) out.rules['*'] = asMessage(b.rules);
    else if (b.rules && typeof b.rules === 'object') {
      for (const [idx, msg] of Object.entries(b.rules as Record<string, unknown>)) {
        // Rule-row messages are self-contained; the field key is not repeated.
        out.rules[idx] =
          msg && typeof msg === 'object' && !Array.isArray(msg)
            ? Object.values(msg as Record<string, unknown>).map(asMessage).join(' ')
            : asMessage(msg);
      }
    }
  }

  if (b.delivery_method_advanced_rules !== undefined) {
    const raw = b.delivery_method_advanced_rules;
    if (typeof raw === 'string' || Array.isArray(raw)) out.advancedRules['*'] = asMessage(raw);
    else if (raw && typeof raw === 'object') {
      for (const [idx, msg] of Object.entries(raw as Record<string, unknown>)) {
        out.advancedRules[idx] = asMessage(msg);
      }
    }
  }

  if (b.auto_replies !== undefined) {
    const raw = b.auto_replies;
    if (typeof raw === 'string' || Array.isArray(raw)) out.autoReplies['*'] = asMessage(raw);
    else if (raw && typeof raw === 'object') {
      const rec = raw as Record<string, unknown>;
      for (const [key, msg] of Object.entries(rec)) {
        if (key === 'options' && msg && typeof msg === 'object' && !Array.isArray(msg)) {
          for (const [idx, optErr] of Object.entries(msg as Record<string, unknown>)) {
            out.autoReplies[idx] = asMessage(optErr);
          }
        } else {
          out.autoReplies['*'] = [out.autoReplies['*'], `${key}: ${asMessage(msg)}`]
            .filter(Boolean)
            .join('; ');
        }
      }
    }
  }

  if (typeof b.detail === 'string') out.general.push(b.detail);

  // Anything else (unknown top-level keys) lands in general.
  for (const key of Object.keys(b)) {
    if (!['notification', 'delivery_methods', 'block_refs', 'rules', 'delivery_method_advanced_rules', 'auto_replies', 'detail', 'warnings'].includes(key)) {
      out.general.push(`${key}: ${asMessage(b[key])}`);
    }
  }
  return out;
}
